import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Avatar,
  Chip, LinearProgress, Tooltip, IconButton, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert
} from '@mui/material';
import {
  FolderSpecial as PortfolioIcon,
  CheckCircle as GreenIcon,
  Warning as OrangeIcon,
  Error as RedIcon,
  Refresh as RefreshIcon,
  ArrowForward as GoIcon,
  Assignment as TaskIcon,
  Group as TeamIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendIcon
} from '@mui/icons-material';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip,
  Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { portfolioService } from '../services/api';
import { useTranslation } from 'react-i18next';

// ─── Constantes ──────────────────────────────────────────────────────────────
const HEALTH_COLORS = { green: '#4caf50', orange: '#ff9800', red: '#f44336' };
const STATUS_COLORS  = { active: '#1976d2', completed: '#4caf50', on_hold: '#9e9e9e' };
const PRIORITY_COLORS = { low: '#8bc34a', medium: '#ff9800', high: '#f44336', urgent: '#9c27b0' };

// ─── Barre de santé globale ───────────────────────────────────────────────────
function GlobalHealthBar({ green, orange, red }) {
  const total = green + orange + red || 1;
  return (
    <Box sx={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', width: '100%' }}>
      {green  > 0 && <Box sx={{ width: `${(green  / total) * 100}%`, bgcolor: HEALTH_COLORS.green  }} />}
      {orange > 0 && <Box sx={{ width: `${(orange / total) * 100}%`, bgcolor: HEALTH_COLORS.orange }} />}
      {red    > 0 && <Box sx={{ width: `${(red    / total) * 100}%`, bgcolor: HEALTH_COLORS.red    }} />}
    </Box>
  );
}

// ─── Puce santé ──────────────────────────────────────────────────────────────
function HealthChip({ health, t }) {
  const labels = { green: t('portfolio.healthGreen'), orange: t('portfolio.healthOrange'), red: t('portfolio.healthRed') };
  const icons  = { green: <GreenIcon sx={{ fontSize: 16 }} />, orange: <OrangeIcon sx={{ fontSize: 16 }} />, red: <RedIcon sx={{ fontSize: 16 }} /> };
  return (
    <Chip
      icon={icons[health]}
      label={labels[health]}
      size="small"
      sx={{ bgcolor: `${HEALTH_COLORS[health]}22`, color: HEALTH_COLORS[health], fontWeight: 'bold', border: `1px solid ${HEALTH_COLORS[health]}44` }}
    />
  );
}

// ─── Timeline Inter-projets ───────────────────────────────────────────────────
function InterProjectTimeline({ projects }) {
  const withDates = projects.filter(p => p.start_date && p.end_date);
  if (withDates.length === 0) return null;

  const toMs = (d) => new Date(d).getTime();
  const minMs = Math.min(...withDates.map(p => toMs(p.start_date)));
  const maxMs = Math.max(...withDates.map(p => toMs(p.end_date)));
  const span  = maxMs - minMs || 1;
  const todayMs = Date.now();
  const todayPct = Math.min(100, Math.max(0, ((todayMs - minMs) / span) * 100));

  const fmt = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleIcon color="primary" /> Timeline inter-projets
      </Typography>

      {/* Axe de dates */}
      <Box sx={{ position: 'relative', mb: 1, pl: '160px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{fmt(new Date(minMs))}</Typography>
          <Typography variant="caption" color="text.secondary">{fmt(new Date(maxMs))}</Typography>
        </Box>
        {/* Curseur "Aujourd'hui" */}
        {todayMs >= minMs && todayMs <= maxMs && (
          <Tooltip title="Aujourd'hui">
            <Box sx={{
              position: 'absolute', left: `calc(160px + ${todayPct}%)`,
              top: -4, bottom: 0,
              borderLeft: '2px dashed #e53935',
              zIndex: 2,
              '&::before': {
                content: '"▼"', position: 'absolute', top: -14, left: -7,
                fontSize: '12px', color: '#e53935'
              }
            }} />
          </Tooltip>
        )}
      </Box>

      {/* Barres de projets */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {withDates.map(p => {
          const leftPct  = ((toMs(p.start_date) - minMs) / span) * 100;
          const widthPct = Math.max(1, ((toMs(p.end_date) - toMs(p.start_date)) / span) * 100);
          const color    = p.color || '#1976d2';
          return (
            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', height: 28 }}>
              {/* Label */}
              <Tooltip title={p.title}>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ width: 152, minWidth: 152, mr: 1, fontWeight: 500, fontSize: '0.75rem' }}
                >
                  {p.title}
                </Typography>
              </Tooltip>
              {/* Piste */}
              <Box sx={{ flexGrow: 1, position: 'relative', height: 20, bgcolor: '#f0f0f0', borderRadius: 2 }}>
                {/* Barre de complétion */}
                <Box sx={{
                  position: 'absolute',
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                  height: '100%',
                  bgcolor: color,
                  opacity: 0.25,
                  borderRadius: 2,
                }} />
                {/* Barre de progression (remplie proportionnellement à la complétion) */}
                <Box sx={{
                  position: 'absolute',
                  left: `${leftPct}%`,
                  width: `${widthPct * (p.completion_rate / 100)}%`,
                  height: '100%',
                  bgcolor: color,
                  borderRadius: 2,
                  transition: 'width 0.5s',
                }} />
                {/* Curseur aujourd'hui */}
                {todayMs >= minMs && todayMs <= maxMs && (
                  <Box sx={{
                    position: 'absolute',
                    left: `${todayPct}%`,
                    top: 0, bottom: 0,
                    borderLeft: '1.5px dashed #e53935',
                    zIndex: 1,
                  }} />
                )}
              </Box>
              {/* % */}
              <Typography variant="caption" sx={{ ml: 1, minWidth: 32, color: 'text.secondary' }}>
                {p.completion_rate}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function PortfolioPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await portfolioService.getData();
      setData(res.data);
    } catch (e) {
      setError(t('portfolio.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" action={<IconButton size="small" onClick={fetchData}><RefreshIcon /></IconButton>}>{error}</Alert>;
  }

  const { kpis, projects } = data;

  // Données pour graphiques
  const statusChartData = [
    { name: t('portfolio.statusActive'),    value: kpis.active_projects,    color: STATUS_COLORS.active },
    { name: t('portfolio.statusCompleted'), value: kpis.completed_projects,  color: STATUS_COLORS.completed },
    { name: t('portfolio.statusOnHold'),    value: kpis.on_hold_projects,    color: STATUS_COLORS.on_hold },
  ].filter(d => d.value > 0);

  const priorityCount = projects.reduce((acc, p) => {
    acc[p.priority] = (acc[p.priority] || 0) + 1;
    return acc;
  }, {});
  const priorityChartData = Object.entries(priorityCount).map(([key, val]) => ({
    name: t(`portfolio.priority_${key}`), value: val, fill: PRIORITY_COLORS[key] || '#90a4ae'
  }));

  const healthSummary = [
    { label: t('portfolio.healthGreen'),  count: kpis.health_green,  color: HEALTH_COLORS.green,  icon: <GreenIcon />  },
    { label: t('portfolio.healthOrange'), count: kpis.health_orange, color: HEALTH_COLORS.orange, icon: <OrangeIcon /> },
    { label: t('portfolio.healthRed'),    count: kpis.health_red,    color: HEALTH_COLORS.red,    icon: <RedIcon />    },
  ];

  return (
    <Box>
      {/* ── En-tête ── */}
      <Paper elevation={0} sx={{
        p: { xs: 2, md: 4 }, mb: 3, borderRadius: 2,
        background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 60%, #1976d2 100%)',
        color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.4rem', md: '2rem' } }}>
            <PortfolioIcon /> {t('portfolio.title')}
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.85 }}>
            {t('portfolio.subtitle', { count: kpis.total_projects })}
          </Typography>
        </Box>
        <Tooltip title={t('portfolio.refresh')}>
          <IconButton onClick={fetchData} sx={{ color: 'white' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* ── KPIs ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('portfolio.kpiTotalProjects'),   value: kpis.total_projects,           icon: <PortfolioIcon />, color: '#1976d2' },
          { label: t('portfolio.kpiActiveProjects'),  value: kpis.active_projects,           icon: <TrendIcon />,     color: '#0288d1' },
          { label: t('portfolio.kpiCompletionRate'),  value: `${kpis.global_completion_rate}%`, icon: <TaskIcon />,   color: '#388e3c' },
          { label: t('portfolio.kpiOverdueTasks'),    value: kpis.overdue_tasks,             icon: <RedIcon />,       color: kpis.overdue_tasks > 0 ? '#d32f2f' : '#388e3c' },
          { label: t('portfolio.kpiTotalTasks'),      value: kpis.total_tasks,               icon: <TaskIcon />,      color: '#7b1fa2' },
        ].map((kpi, i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Avatar sx={{ bgcolor: `${kpi.color}22`, color: kpi.color, mb: 1, width: 36, height: 36 }}>
                  {kpi.icon}
                </Avatar>
                <Typography variant="h4" fontWeight="bold" sx={{ color: kpi.color, fontSize: { xs: '1.4rem', md: '1.8rem' } }}>
                  {kpi.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Santé globale ── */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('portfolio.globalHealthTitle')}
        </Typography>
        <GlobalHealthBar green={kpis.health_green} orange={kpis.health_orange} red={kpis.health_red} />
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          {healthSummary.map(h => (
            <Box key={h.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: h.color, display: 'flex' }}>{h.icon}</Box>
              <Typography variant="body2" fontWeight="bold" sx={{ color: h.color }}>{h.count}</Typography>
              <Typography variant="body2" color="text.secondary">{h.label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* ── Timeline ── */}
      <Box sx={{ mb: 3 }}>
        <InterProjectTimeline projects={projects} />
      </Box>

      {/* ── Tableau des projets ── */}
      <Paper elevation={2} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
          <Typography variant="h6" fontWeight="bold">{t('portfolio.projectsTableTitle')}</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><Typography variant="caption" fontWeight="bold">{t('portfolio.colProject')}</Typography></TableCell>
                <TableCell><Typography variant="caption" fontWeight="bold">{t('portfolio.colHealth')}</Typography></TableCell>
                <TableCell><Typography variant="caption" fontWeight="bold">{t('portfolio.colProgress')}</Typography></TableCell>
                <TableCell><Typography variant="caption" fontWeight="bold">{t('portfolio.colStatus')}</Typography></TableCell>
                <TableCell><Typography variant="caption" fontWeight="bold">{t('portfolio.colPriority')}</Typography></TableCell>
                <TableCell><Typography variant="caption" fontWeight="bold">{t('portfolio.colTasks')}</Typography></TableCell>
                <TableCell><Typography variant="caption" fontWeight="bold">{t('portfolio.colTeam')}</Typography></TableCell>
                <TableCell><Typography variant="caption" fontWeight="bold">{t('portfolio.colDueDate')}</Typography></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map(p => (
                <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${p.id}`)}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: p.color || '#1976d2', width: 28, height: 28, fontSize: '0.8rem' }}>
                        {p.title.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 180 }}>
                        {p.title}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell><HealthChip health={p.health} t={t} /></TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={p.completion_rate}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                        color={p.health === 'red' ? 'error' : p.health === 'orange' ? 'warning' : 'success'}
                      />
                      <Typography variant="caption">{p.completion_rate}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`portfolio.status_${p.status}`)}
                      size="small"
                      sx={{ bgcolor: `${STATUS_COLORS[p.status] || '#9e9e9e'}22`, color: STATUS_COLORS[p.status] || '#9e9e9e', fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`portfolio.priority_${p.priority}`)}
                      size="small"
                      sx={{ bgcolor: `${PRIORITY_COLORS[p.priority] || '#90a4ae'}22`, color: PRIORITY_COLORS[p.priority] || '#90a4ae', fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TaskIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption">{p.completed_tasks}/{p.total_tasks}</Typography>
                      {p.overdue_tasks > 0 && (
                        <Chip label={`${p.overdue_tasks} retard`} size="small" color="error" sx={{ height: 16, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TeamIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption">{p.team_members}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color={p.health === 'red' ? 'error.main' : 'text.secondary'}>
                      {p.end_date ? new Date(p.end_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/projects/${p.id}`); }}>
                      <GoIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {projects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t('portfolio.noProjects')}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Graphiques ── */}
      <Grid container spacing={3}>
        {/* Répartition par statut */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>{t('portfolio.chartStatusTitle')}</Typography>
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="value">
                    {statusChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{t('portfolio.noData')}</Typography>
            )}
          </Paper>
        </Grid>

        {/* Répartition par priorité */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>{t('portfolio.chartPriorityTitle')}</Typography>
            {priorityChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={priorityChartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {priorityChartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{t('portfolio.noData')}</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
